
import { usePresetsStore } from "@/stores/ssaStore/presets";
import { storeToRefs } from "pinia";
import { float } from "vtk.js/Sources/types";
import { defineComponent } from "vue";
import ColorPicker from "../colorPicker.vue";
import { useActiveStore } from "@/stores/ssaStore/active";

class GradientStop {
	active = false;
	color = "#ffffff";
	stop = 0;
	id = "";
	annotation = "";
	constructor(color: string, stop: float, annotation = "") {
		this.color = color;
		this.annotation = annotation;
		this.stop = stop;
		const guidGenerator = () => {
			const S4 = () => {
				return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
			};
			return S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4();
		};
		this.id = guidGenerator();
	}
	getRbg() {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(this.color);
		return {
			r: parseInt(result![1], 16),
			g: parseInt(result![2], 16),
			b: parseInt(result![3], 16)
		};
	}
}
export default defineComponent({
	name: "imdex-gradient-editor",
	components: {
		ColorPicker
	},
	setup() {
		const { SSA_SAVE_COLOR_PRESET: savePreset, SSA_DELETE_COLOR_PRESET: gradientDelete } =
			usePresetsStore();
		const { SSA_ACTIVE_REPRESENTATION_STATE: activeRepresentationState } = storeToRefs(
			useActiveStore()
		);
		const { SSA_PRESETS_ITEMS: presetList, colorScale } = storeToRefs(usePresetsStore());

		return {
			activeRepresentationState,
			colorScale,
			savePreset,
			presetList,
			gradientDelete
		};
	},
	props: {
		gradientItem: {
			type: Object as any,
			default: () => ({}) as any
		},
		isFixed: {
			type: Boolean,
			default: false
		}
	},
	watch: {
		gradientItem: {
			deep: true,
			handler(v) {
				this.init();
			}
		}
	},
	mounted() {
		this.init();
	},
	data() {
		return {
			gradientName: "",
			activeGradientItem: "",
			gradient: [] as any,
			colorPickerOpen: false,
			outsideClickListener: null as any,
			moveMouse: null as any,
			showOptionsMenu: false,
			errorMessage: "",
			deleteGradientLoading: false,
			deleteGradientDialog: false
		};
	},
	beforeUnmount() {
		this.showOptionsMenu = false;
		this.$nextTick(() => {
			const container: any = window.document.getElementById("pane-container");
			const els = container.getElementsByClassName("color-picker");
			for (let i = 0; i < els.length; i++) {
				container.removeChild(els[i]);
			}
		});
	},
	methods: {
		validateGradient() {
			if (this.gradientName.length > 25) {
				this.errorMessage = this.$t("ssa.displayPane.colorGeneral.errorLength");
				return false;
			}
			if (this.gradientName == this.$t("ssa.displayPane.colorGeneral.newGradient")) {
				this.errorMessage = `${this.$t(
					"ssa.displayPane.colorGeneral.errorDefaultName"
				)} '${this.$t("ssa.displayPane.colorGeneral.newGradient")}'`;
				return false;
			}
			if (this.gradientName == this.$t("ssa.displayPane.colorGeneral.newFixedGradient")) {
				this.errorMessage = `${this.$t(
					"ssa.displayPane.colorGeneral.errorDefaultName"
				)} '${this.$t("ssa.displayPane.colorGeneral.newFixedGradient")}'`;
				return false;
			}

			let annotationFlag = false;
			let allannotations = [];
			if (this.isFixed) {
				allannotations = this.fixedGradient.map((item: any) => {
					return item.annotation;
				});
			} else {
				allannotations = this.gradient.map((item: any) => {
					return item.annotation;
				});
			}

			for (let i = 0; i < allannotations.length; i++) {
				if (allannotations[i].length > 20) {
					annotationFlag = true;
				}
			}
			if (annotationFlag) {
				this.errorMessage = this.$t("ssa.displayPane.colorGeneral.errorAnnoLength");
				return false;
			}

			const isNameTooShort = this.gradientName && this.gradientName.length < 3;

			if (
				this.gradientName == "" ||
				(this.gradientName && !this.gradientName.trim()) ||
				isNameTooShort
			) {
				if (this.gradientName == "") {
					this.errorMessage = this.$t("ssa.displayPane.colorGeneral.errorNameBlank");
				} else if (isNameTooShort) {
					this.errorMessage = this.$t("ssa.displayPane.colorGeneral.errorNameShort");
				} else {
					this.errorMessage = this.$t("ssa.displayPane.colorGeneral.errorNameBlank");
				}
				return false;
			}

			return true;
		},
		isSelected(id: string) {
			return (
				this.activeRepresentationState &&
				this.activeRepresentationState.coloring.preset.id == id
			);
		},
		guidGenerator() {
			const S4 = () => {
				return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
			};
			return S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4();
		},
		changeStop(item: any, event: any, changingMax = false) {
			if (this.isFixed) {
				const minId = item.minId;
				const maxId = item.maxId;
				const minIdx = this.gradient.findIndex((i: any) => i.id == item.minId);
				const maxIdx = this.gradient.findIndex((i: any) => i.id == item.maxId);

				if (minIdx == 0) {
					if (changingMax) {
						if (event < this.gradient[maxIdx + 2].stop) {
							this.gradient[maxIdx].stop = event;
							this.gradient[maxIdx + 1].stop = event;
						}
					} else {
						if (event < this.gradient[maxIdx].stop) {
							this.gradient[minIdx].stop = event;
						}
					}
				} else if (maxIdx == this.gradient.length - 1) {
					if (changingMax) {
						if (event > this.gradient[minIdx].stop) {
							this.gradient[maxIdx].stop = event;
						}
					} else {
						if (event > this.gradient[minIdx - 2].stop) {
							this.gradient[minIdx].stop = event;
							this.gradient[minIdx - 1].stop = event;
						}
					}
				} else {
					if (changingMax) {
						if (
							event < this.gradient[maxIdx + 2].stop &&
							event > this.gradient[minIdx].stop
						) {
							this.gradient[maxIdx].stop = event;
							this.gradient[maxIdx + 1].stop = event;
						}
					} else {
						if (
							event > this.gradient[minIdx - 2].stop &&
							event < this.gradient[maxIdx].stop
						) {
							this.gradient[minIdx].stop = event;
							this.gradient[minIdx - 1].stop = event;
						}
					}
				}
			} else {
				const idx = this.gradient.findIndex((i: any) => i.id == item.id);
				switch (idx) {
					case 0: {
						const maxTest = this.gradient[idx + 1].stop;
						if (event < maxTest) {
							item.stop = event;
							if (this.isFixed) {
								this.gradient[
									this.gradient.findIndex((item2: any) => item2.id == item.id) - 1
								].stop = event;
							}
						}
						break;
					}
					case this.gradient.length - 1: {
						const minTest = this.gradient[idx - 1].stop;

						if (minTest <= event) {
							item.stop = event;
							if (this.isFixed) {
								this.gradient[
									this.gradient.findIndex((item2: any) => item2.id == item.id) - 1
								].stop = event;
							}
						}
						break;
					}
					default: {
						const minTest = this.gradient[idx - 1].stop;
						const maxTest = this.gradient[idx + 1].stop;
						if (minTest <= event && event <= maxTest) {
							item.stop = event;
							if (this.isFixed) {
								this.gradient[
									this.gradient.findIndex((item2: any) => item2.id == item.id) - 1
								].stop = event;
							}
						}
					}
				}
			}
		},
		pickStop(e: any) {
			const vals = [] as number[];
			for (let i = 0; i < this.gradient.length; i++) {
				vals.push(Number(this.gradient[i].stop));
			}
			const min: number = Math.min(...vals);
			const max: number = Math.max(...vals);
			const bar = this.$refs.gradientBar as any;
			const { width } = bar.getBoundingClientRect();
			const ratio = e.offsetX / width;

			let color1;
			let color2;

			for (let i = 0; i < this.gradient.length; i++) {
				if (i == this.gradient.length - 1) break;
				const testRatio = (this.gradient[i].stop - min) / (max - min);
				const testRatio2 = (this.gradient[i + 1].stop - min) / (max - min);
				if (testRatio < ratio && ratio < testRatio2) {
					color1 = this.gradient[i];
					color2 = this.gradient[i + 1];
				}
			}

			const d1 = (color1.stop - min) / (max - min);
			const d2 = (color2.stop - min) / (max - min);
			const px = ((color1.stop - min) / (max - min)) * width;
			const x2 = e.offsetX - px;
			const smallWidth = width - width * d1 - (width - width * d2);
			this.addColorStop(x2 / smallWidth, color1, color2);
		},
		init() {
			const rgbPoints = this.gradientItem.rgbPoints;
			const min = rgbPoints[0] as number;
			const max = rgbPoints[rgbPoints.length - 4] as number;
			this.gradient = [];

			for (let i = 0; i < rgbPoints.length; i += 4) {
				const [val, r, g, b] = [
					rgbPoints[i],
					rgbPoints[i + 1],
					rgbPoints[i + 2],
					rgbPoints[i + 3]
				] as any;

				const stop = new GradientStop(
					this.rgbToHex(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)),
					Math.round(val * 100) / 100
				) as any;

				this.gradient.push(stop);
			}
			let i2 = 0;
			if (this.gradientItem.annotations != null) {
				for (let i = 1; i < this.gradientItem.annotations.length; i += 2) {
					if (this.gradient[i2] != null) {
						this.gradient[i2].annotation = this.gradientItem.annotations[i];
					}
					i2 += 2;
				}
			}
			this.gradientName = this.gradientItem.text;
		},
		saveGradient() {
			if (!this.validateGradient()) return;
			let rgbPoints = [] as number[];
			for (let i = 0; i < this.gradient.length; i++) {
				const stop = this.gradient[i].stop;
				const { r, g, b } = this.hexToRgb(this.gradient[i].color);
				rgbPoints = rgbPoints.concat([stop, r / 255, g / 255, b / 255]);
			}
			let annotations = [] as any;
			if (this.isFixed) {
				for (let i = 0; i < this.gradient.length - 1; i += 2) {
					const stop = this.gradient[i].stop;
					const nextStop = this.gradient[i + 1].stop;

					annotations = annotations.concat([
						((stop + nextStop) / 2).toString(),
						this.gradient[i].annotation
					]);
				}
			}

			const preset = {
				...(this.gradientItem as any),
				text: this.gradientName,
				value: this.gradientName,
				rgbPoints,
				annotations
			};

			if (preset.id.indexOf("-") < 3) {
				delete preset.id;
				preset.id = this.guidGenerator();
			}

			const presetNameList = this.presetList.map((item: any) => item.text);
			if (presetNameList.includes(preset.text)) {
				const count = presetNameList.filter(
					(item: any) => item.split(" (")[0] == preset.text.split(" (")[0]
				).length;
				if (this.presetList[presetNameList.indexOf(preset.text)].id != preset.id) {
					preset.text = `${preset.text}` + ` (${count})`;
				}
				// preset.text = `${preset.text} (copy)`
			}
			// delete preset.id
			this.savePreset(preset, this.isFixed);
			this.showOptionsMenu = false;
			this.$nextTick(() => {
				this.$emit("saved");
			});
		},
		deleteGradient() {
			this.deleteGradientLoading = true;
			this.gradientDelete(this.gradientItem.id, this.isFixed);
			this.$emit("saved");
			this.deleteGradientDialog = false;
			this.deleteGradientLoading = false;
		},
		openColorPicker(colorName: any, block = false) {
			this.$nextTick(() => {
				this.colorPickerOpen = !this.colorPickerOpen;
				const container: any = window.document.getElementById("pane-container");
				if (this.colorPickerOpen) {
					this.$nextTick(() => {
						window.addEventListener(
							"mousedown",
							((this.outsideClickListener as any) = (e: any) => {
								const [x, y] = [e.pageX, e.pageY];
								const elements = document.elementsFromPoint(x, y);
								for (let i = 0; i < elements.length; i++) {
									if (elements[i].classList.contains("color-picker")) {
										return;
									}
								}
								setTimeout(() => {
									this.colorPickerOpen = false;
								}, 100);

								window.removeEventListener(
									"mousedown",
									this.outsideClickListener as any
								);
							})
						);

						const { left, top, width } = (
							this.$refs[block ? `block-${colorName}` : colorName] as any
						)[0].getBoundingClientRect();
						const { height: elHeight } = (
							this.$refs.colorPicker as any
						).$el.getBoundingClientRect();

						if (left - 250 < 0) {
							(this.$refs as any).colorPicker.$el.style.left = left + width + "px";
						} else {
							(this.$refs as any).colorPicker.$el.style.left = left - 250 + "px";
						}
						if (top + elHeight > window.innerHeight) {
							(this.$refs as any).colorPicker.$el.style.top =
								window.innerHeight - elHeight - 15 + "px";
						} else {
							(this.$refs as any).colorPicker.$el.style.top = top + "px";
						}

						container.appendChild((this.$refs.colorPicker as any).$el);
					});
				}
			});
		},
		hexToRgb(hex: string) {
			const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
			return {
				r: parseInt(result![1], 16),
				g: parseInt(result![2], 16),
				b: parseInt(result![3], 16)
			};
		},
		rgbToHex(r: number, g: number, b: number) {
			const componentToHex = (c: any) => {
				const hex = c.toString(16);
				return hex.length == 1 ? "0" + hex : hex;
			};
			return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
		},
		removeColorStop(id: string) {
			if (this.isFixed) {
				const activeFixedColor =
					this.fixedGradient[
						this.fixedGradient.findIndex(
							(item: any) => item.minId == id || item.maxId == id
						)
					];
				if (activeFixedColor == null) return;

				if (this.gradient.length > 4) {
					const minIdx = this.gradient.findIndex(
						(item: any) => item.id == activeFixedColor.minId
					);
					const maxIdx = this.gradient.findIndex(
						(item: any) => item.id == activeFixedColor.maxId
					);
					if (maxIdx != this.gradient.length - 1) {
						this.gradient[minIdx - 1].stop = this.gradient[maxIdx + 1].stop;
					}

					this.gradient.splice(
						this.gradient.findIndex((item: any) => item.id == activeFixedColor.minId),
						1
					);
					this.gradient.splice(
						this.gradient.findIndex((item: any) => item.id == activeFixedColor.maxId),
						1
					);
				}
			} else {
				if (this.gradient.length > 2) {
					return this.gradient.splice(
						this.gradient.findIndex((item: any) => item.id == id),
						1
					);
				}
			}
		},
		startDrag(e: MouseEvent) {
			e.preventDefault();
			this.$nextTick(() => {
				// const {pageX} = e;

				const activeColor =
					this.gradient[
						this.gradient.findIndex((item: any) => item.id == this.activeGradientItem)
					];

				const bar = this.$refs.gradientBar as any;
				const { width, left, top, height } = bar.getBoundingClientRect();

				this.moveMouse = (e: MouseEvent) => {
					const { pageX, pageY } = e;
					let stopPerc = (pageX - left) / width;
					if (stopPerc < 0) {
						stopPerc = 0;
					}
					if (stopPerc > 1) {
						stopPerc = 1;
					}

					if (!this.isFixed) {
						if (
							this.gradient.findIndex((item: any) => item.id == activeColor.id) ==
								0 ||
							this.gradient.findIndex((item: any) => item.id == activeColor.id) ==
								this.gradient.length - 1
						) {
							return;
						}
						//need to calculate min and max bounds
						const nextColor =
							this.gradient[
								this.gradient.findIndex((item: any) => item.id == activeColor.id) +
									1
							];
						const prevColor =
							this.gradient[
								this.gradient.findIndex((item: any) => item.id == activeColor.id) -
									1
							];
						const stopVal =
							(Math.round(stopPerc * 100) * (this.range[1] - this.range[0])) / 100 +
							this.range[0];
						if (stopVal < prevColor.stop || stopVal > nextColor.stop) {
							return;
						}

						activeColor.stop = Math.round(stopVal * 100) / 100;
						if (pageY > top + height + 50) {
							this.removeColorStop(this.activeGradientItem);
							this.endDrag();
						}
					} else {
						if (
							this.gradient.findIndex((item: any) => item.id == activeColor.id) ==
								0 ||
							this.gradient.findIndex((item: any) => item.id == activeColor.id) ==
								this.gradient.length - 1
						) {
							return;
						}
						const activeColorPair =
							this.gradient[
								this.gradient.findIndex((item: any) => item.id == activeColor.id) -
									1
							];
						// (((this.range[1]-this.range[0])/this.range[1]))
						// console.log((Math.round(stopPerc * 100) * (this.range[1] - this.range[0])) / 100);
						const nextColor =
							this.gradient[
								this.gradient.findIndex((item: any) => item.id == activeColor.id) +
									1
							];
						const prevColor =
							this.gradient[
								this.gradient.findIndex((item: any) => item.id == activeColor.id) -
									2
							];
						const stopVal =
							(Math.round(stopPerc * 100) * (this.range[1] - this.range[0])) / 100 +
							this.range[0];
						if (stopVal < prevColor.stop || stopVal > nextColor.stop) {
							return;
						}

						activeColor.stop = Math.round(stopVal * 100) / 100;
						activeColorPair.stop = Math.round(stopVal * 100) / 100;

						if (pageY > top + height + 50) {
							this.removeColorStop(activeColor.id);
							this.endDrag();
						}
					}
				};
				window.addEventListener("mousemove", this.moveMouse);
				window.addEventListener("mouseup", this.endDrag);
			});
		},
		endDrag() {
			window.removeEventListener("mousemove", this.moveMouse);
			window.removeEventListener("mouseup", this.endDrag);
		},
		addColorStop(ratio = 0, forceColor1 = null as any, forceColor2 = null as any) {
			const pickRgb = (color1: any, color2: any, weight: any) => {
				const p = weight;
				const w = p * 2 - 1;
				const w1 = (w / 1 + 1) / 2;
				const w2 = 1 - w1;
				const rgb = [
					Math.round(color1.r * w1 + color2.r * w2),
					Math.round(color1.g * w1 + color2.g * w2),
					Math.round(color1.b * w1 + color2.b * w2)
				];
				return rgb;
			};

			if (this.activeGradientItem != "") {
				if (ratio != 0) {
					const idx = this.gradient.findIndex((item: any) => item.id == forceColor1.id);

					const color1 = forceColor1;
					const color2 = forceColor2;
					const stopdif =
						Number(color2.stop) - Number(color1.stop) > 0
							? (Number(color2.stop) - Number(color1.stop)) * ratio +
							  Number(color1.stop)
							: (Number(color1.stop) - Number(color2.stop)) * ratio +
							  Number(color2.stop);

					const rgb = pickRgb(color1.getRbg(), color2.getRbg(), 1 - ratio);
					const hex = this.rgbToHex(rgb[0], rgb[1], rgb[2]);
					if (this.isFixed) {
						this.gradient.splice(
							idx + 1,
							0,
							...[new GradientStop(hex, stopdif), new GradientStop(hex, stopdif)]
						);
					} else {
						this.gradient.splice(idx + 1, 0, new GradientStop(hex, stopdif));
					}
				} else {
					const idx = this.gradient.findIndex(
						(item: any) => item.id == this.activeGradientItem
					);

					const dir = idx == this.gradient.length - 1 ? -1 : 1;

					const color1 = this.gradient[idx];
					const color2 = this.gradient[idx + dir];
					const stopdif =
						Number(color2.stop) - Number(color1.stop) > 0
							? (Number(color2.stop) - Number(color1.stop)) / 2 + Number(color1.stop)
							: (Number(color1.stop) - Number(color2.stop)) / 2 + Number(color2.stop);

					const rgb = pickRgb(color1.getRbg(), color2.getRbg(), 0.5);
					const hex = this.rgbToHex(rgb[0], rgb[1], rgb[2]);
					if (!this.isFixed) {
						this.gradient.splice(
							idx + (dir < 0 ? 0 : 1),
							0,
							new GradientStop(hex, stopdif)
						);
					} else {
						this.gradient.splice(
							idx + (dir < 0 ? 0 : 1),
							0,
							...[new GradientStop(hex, stopdif), new GradientStop(hex, stopdif)]
						);
					}
				}
			}
		},
		pickColor(e: any) {
			if (this.isFixed) {
				this.gradient[
					this.gradient.findIndex((item: any) => item.id == this.activeGradientItem)
				].color = e.color;
				this.gradient[
					this.gradient.findIndex((item: any) => item.id == this.activeGradientItem) + 1
				].color = e.color;
			} else {
				this.gradient[
					this.gradient.findIndex((item: any) => item.id == this.activeGradientItem)
				].color = e.color;
			}
		}
	},
	computed: {
		fixedGradient: {
			get() {
				const g = this.gradient;
				const fg = [] as any;
				for (let i = 0; i < g.length; i += 2) {
					const obj = {
						color: g[i].color,
						minId: g[i].id,
						maxId: g[i + 1].id,
						min: g[i].stop,
						max: g[i + 1].stop,
						active: false,
						annotation: g[i].annotation
					};
					fg.push(obj);
				}
				return fg;
			},
			set(v: any) {
				console.log(v);
			}
		},
		isDefaultGradient() {
			return (this.gradientItem.id.match(/-/g) || []).length < 4;
		},
		activeColor(): any {
			return this.gradient[
				this.gradient.findIndex((item: any) => item.id == this.activeGradientItem)
			];
		},
		range(): any {
			const stops = this.gradient.map((item: any) => item.stop);
			const min = Math.min(...stops);
			const max = Math.max(...stops);
			return [min, max];
		},
		gradientScheme(): any {
			const arr = [] as string[];

			for (let i = 0; i < this.gradient.length; i++) {
				const val = `${this.gradient[i].color} ${
					((this.gradient[i].stop - this.range[0]) / (this.range[1] - this.range[0])) *
						100 +
					"%"
				}`;
				arr.push(val);
			}

			return `linear-gradient(90deg, ${arr.join(",")})`;
		}
	}
});
